import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const values = [
  {
    icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
    title: 'Innovation First',
    description: 'We embrace emerging technologies and unconventional approaches to solve problems that established methods cannot.'
  },
  {
    icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
    title: 'Client Partnership',
    description: 'We build lasting relationships through radical transparency, genuine collaboration, and a shared commitment to your success.'
  },
  {
    icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>,
    title: 'Quality Excellence',
    description: 'We maintain the highest standards in code quality, security, and performance across every single deliverable.'
  },
  {
    icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
    title: 'Agile Delivery',
    description: 'Iterative, transparent development that adapts to your evolving needs and delivers measurable value from day one.'
  },
  {
    icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
    title: 'Security by Design',
    description: 'Security is not an afterthought — it is engineered into every layer of every solution we build, from architecture to deployment.'
  },
  {
    icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    title: 'Global Perspective',
    description: 'Headquartered in Dubai with engineering talent across the globe, we bring a diverse, world-class perspective to every project.'
  }
];

const milestones = [
  { year: '2025', title: 'Founded in Dubai', desc: 'Ascali IT Service LLC established in the UAE with a mission to manufacture the digital future of businesses.' },
  { year: '2025', title: 'First Enterprise Clients', desc: 'Delivered our first Cloud ERP and custom web application solutions to clients across the UAE and India.' },
  { year: '2025', title: 'Global Expansion', desc: 'Expanded service delivery capabilities with engineering centers in India, supporting clients across 10+ countries.' },
  { year: '2026', title: 'AI & Analytics Division', desc: 'Launched dedicated Data Analytics and AI practice, bringing machine learning capabilities to enterprise clients.' }
];

const AboutUsPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const fallback = setTimeout(() => setIsVisible(true), 120);
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { clearTimeout(fallback); setIsVisible(true); } },
      { threshold: 0.05 }
    );
    if (contentRef.current) observer.observe(contentRef.current);
    return () => {
      clearTimeout(fallback);
      if (contentRef.current) observer.unobserve(contentRef.current);
    };
  }, []);

  return (
    <>
      <title>About Ascali | Software Development &amp; IT Solutions Dubai, UAE</title>
      <meta name="description" content="Learn about Ascali, a Dubai-based software development and IT solutions company helping businesses grow through custom software, cloud technologies, AI, and digital transformation services." />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10 text-center text-white">
          <span className="inline-block text-white/70 text-xs font-semibold uppercase tracking-widest mb-4 bg-white/10 px-4 py-1.5 rounded-full">
            Our Story
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            About <span className="gradient-text">Ascali</span>
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 mx-auto mb-6"></div>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
            We are a dynamic software manufacturing powerhouse headquartered in Dubai, UAE — empowering businesses with scalable, secure, and user-centric digital solutions.
          </p>
        </div>
      </section>

      {/* Metrics */}
      <section className="py-10 bg-white border-b border-slate-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '98%', label: 'Client Satisfaction Rate' },
              { value: '200+', label: 'Projects Delivered' },
              { value: '99.9%', label: 'System Uptime' },
              { value: '10+', label: 'Countries Served' }
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-3xl md:text-4xl font-bold gradient-text font-display mb-1">{stat.value}</div>
                <div className="text-slate-500 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div ref={contentRef}>
        {/* Who We Are */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className={`space-y-6 ${isVisible ? 'animate-slideInLeft' : 'opacity-0'}`}>
                <div>
                  <span className="text-blue-600 text-sm font-semibold uppercase tracking-widest">Who We Are</span>
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
                    Building the Digital Future with Precision and Innovation
                  </h2>
                  <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mb-6"></div>
                </div>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Established in 2025, Ascali IT Service LLC is a dynamic software manufacturing and sales powerhouse headquartered in the heart of Dubai, UAE. We specialize in the end-to-end creation of high-performance digital solutions, ranging from robust Cloud ERP systems to bespoke web and mobile applications.
                </p>
                <p className="text-slate-600 text-lg leading-relaxed">
                  As a forward-thinking technology partner, we bridge the gap between complex engineering and user-centric design. Our expertise lies in manufacturing scalable software that empowers businesses to streamline operations, enhance financial accuracy, and drive growth in an increasingly competitive global market.
                </p>
                <p className="text-slate-600 text-lg leading-relaxed">
                  By leveraging our strategic location in Dubai's thriving tech ecosystem, we deliver cutting-edge products and dedicated support to a diverse range of clients. At Ascali IT Service LLC, we don't just build software — we manufacture the digital future of your business with precision, security, and innovation.
                </p>
              </div>
              <div className={`space-y-6 ${isVisible ? 'animate-slideInRight' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-lg mb-2">Our Vision</h3>
                      <p className="text-slate-600 leading-relaxed">Manufacturing the digital future through innovative, secure, and scalable high-performance software solutions that empower businesses worldwide.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-lg mb-2">Our Mission</h3>
                      <p className="text-slate-600 leading-relaxed">Empowering global businesses by delivering scalable, secure, and user-centric software through technical excellence, partnership, and relentless innovation.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl p-8 text-white">
                  <h3 className="font-bold text-xl mb-4">Headquartered in Dubai</h3>
                  <div className="space-y-2 text-white/80 text-sm">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      United Arab Emirates | India
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                      +971 564 173 186
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      info@ascaliit.com
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className={`text-center mb-14 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              <span className="text-blue-600 text-sm font-semibold uppercase tracking-widest">What Drives Us</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">Our Core Values</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, i) => (
                <div
                  key={i}
                  className={`bg-slate-50 rounded-2xl p-7 card-hover group ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}
                  style={{ animationDelay: `${i * 0.07}s` }}
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform duration-300">
                    {value.icon}
                  </div>
                  <h3 className="font-bold text-lg text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Journey */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <div className={`text-center mb-14 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              <span className="text-blue-600 text-sm font-semibold uppercase tracking-widest">Our Journey</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">Key Milestones</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto"></div>
            </div>
            <div className="max-w-3xl mx-auto">
              {milestones.map((m, i) => (
                <div
                  key={i}
                  className={`flex gap-6 mb-10 ${isVisible ? 'animate-slideInLeft' : 'opacity-0'}`}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {m.year}
                    </div>
                    {i < milestones.length - 1 && <div className="w-0.5 flex-1 bg-gradient-to-b from-blue-300 to-transparent mt-2"></div>}
                  </div>
                  <div className="pt-2 pb-6">
                    <h3 className="font-bold text-slate-900 text-lg mb-2">{m.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Ascali */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className={`bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-10 md:p-16 text-white ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-12">Why Choose Ascali?</h2>
              <div className="grid md:grid-cols-3 gap-10">
                {[
                  { title: 'Deep Technical Expertise', desc: '8+ years of experience across multiple industries with a proven track record of delivering complex, scalable solutions that drive business growth.', color: 'text-cyan-300' },
                  { title: 'End-to-End Solutions', desc: 'From initial concept to ongoing support, we provide comprehensive services that cover every aspect of your digital transformation journey.', color: 'text-cyan-300' },
                  { title: 'Future-Proof Technology', desc: 'We leverage cutting-edge technologies and industry best practices to ensure your solutions remain competitive and scalable for years to come.', color: 'text-cyan-300' }
                ].map((item, i) => (
                  <div key={i}>
                    <h4 className={`font-semibold text-xl mb-3 ${item.color}`}>{item.title}</h4>
                    <p className="text-gray-300 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-slate-50">
          <div className="container-custom text-center">
            <div className={`max-w-2xl mx-auto ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4">Ready to Work Together?</h2>
              <p className="text-slate-600 text-lg mb-8">Let's discuss how Ascali can help transform your business with the right technology solutions.</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact-us"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-full font-semibold text-sm transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                >
                  Get in Touch
                </Link>
                <Link
                  to="/services"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-10 py-4 rounded-full font-semibold text-sm transition-all duration-300"
                >
                  Our Services
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default AboutUsPage;
